import { CandidateStageOverview, type SignalCounts, type StageView } from "./CandidateStageOverview";
import { DATE_PRESETS, type DateFilter, type DatePreset, dateRange, mergeFeed, signalKey, signalsUnavailable } from "./candidateFeed";
import { DetectionControls } from "./DetectionControls";
import { DetectionHistory } from "./DetectionHistory";
import { DroppedSignalCard, SignalStatusBreakdown } from "./DroppedSignalCard";
import { FeedbackOnlyPanel, feedbackOnlyCount, useFeedbackOnly } from "./FeedbackOnlyPanel";
import { RetryButton, RetryNote, type RetryLine } from "./RetryButton";
import { candidateFailures, verdictLabel } from "./secondStageFlow";
import { useCallback, useEffect, useRef, useState } from "react";
import type { AdminApi } from "@/api";
import type { SamsAccessState } from "@/components/layout/AdminShellContext";
import { Badge, Banner, Button, Card } from "@/components/ui";
import type {
  Candidate,
  CandidateOverview,
  CandidatePosition,
  CandidateSignal,
  CandidateSourceEnvironment,
  CandidateStatus,
  JsonObject,
} from "@/types/issuePipeline";
import {
  conversationHref,
  Field,
  inputClass,
  JsonDetails,
  parseObject,
  pipelineError,
} from "./pipelineShared";

const STATUSES: Record<CandidateStatus, string> = {
  pending: "처리 대기",
  needs_completion: "보완 필요",
  promoted: "TC 승격",
  rejected: "제외",
  linked: "기존 TC 연결",
};

/** Where a candidate came from, in the reviewer's words. */
function sourceLabel(item: Candidate) {
  if (item.issue_key === "negative_feedback") return "👎 사용자";
  if (item.issue_key.startsWith("ai_")) return "AI 피드백";
  if (item.issue_key === "manual") return "수동 등록";
  if (item.issue_key === "manual_promotion") return "수동 승격";
  return item.issue_key;
}

function positionOf(item: Candidate): CandidatePosition {
  if (item.status === "promoted") return "tc";
  return item.pipeline_stage === "second" ? "second" : "first";
}

const POSITION_LABELS: Record<CandidatePosition, string> = { first: "후보", second: "이전 방식(2차)", tc: "TC" };

/**
 * Still owed a TC: 1.1차 passes, 👎s and the ones left waiting for the retired
 * 2차 alike. Their stage is history, so their badge says what they wait for.
 */
function waitsForTc(item: Candidate) {
  return (item.status === "pending" || item.status === "needs_completion") && !item.test_case_id;
}

function badgeLabel(item: Candidate, where: CandidatePosition) {
  return where !== "tc" && waitsForTc(item) ? "TC 대기" : POSITION_LABELS[where];
}

/** `?candidate=<id>` opens that candidate — the TC drawer links here. */
function candidateFromUrl() {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("candidate");
}

function FeedbackSummary({ feedback, legacy }: { feedback: unknown; legacy: unknown }) {
  const value = feedback && typeof feedback === "object" && !Array.isArray(feedback) ? feedback as JsonObject : null;
  if (value) return (
    <section className="space-y-2 rounded border p-3 text-sm" aria-label="AI 피드백">
      <h3 className="font-medium">AI 피드백 · 사용자 불편 확인</h3>
      <p>{String(value.description || "")}</p>
      <p>문제 유형: {Array.isArray(value.categories) ? value.categories.join(" · ") : "—"}</p>
      <p>사용자가 기대한 동작: {String(value.expected_behavior || "확인되지 않음")}</p>
      <p className="text-muted-foreground">1.1차에서 확인한 불편 기록입니다. TC 생성 여부는 후보 상태와 연결된 TC에서 확인하세요.</p>
    </section>
  );
  if (!legacy || typeof legacy !== "object" || Array.isArray(legacy)) return null;
  const judgment = legacy as JsonObject;
  return (
    <section className="space-y-2 rounded border p-3 text-sm" aria-label="이전 AI 판단 기록">
      <h3 className="font-medium">이전 AI 판단 기록</h3>
      <p>{String(judgment.candidate_reason || "")}</p>
      <p className="text-muted-foreground">이 기록은 이전 TC 적합성 심사 결과입니다. 현재 1차 피드백 채택 기준으로 사용하지 않습니다.</p>
      <JsonDetails title="이전 판단 원문" value={judgment} />
    </section>
  );
}

/** Why [신호 탐지 실행] is off for this login, or null when it may run (SAMS checks each call again). */
export function detectionBlocked(access: SamsAccessState): string | null {
  if (access.state === "admin") return null;
  if (access.state === "loading") return "권한을 확인하는 중입니다.";
  return `신호 탐지는 SAMS 관리자만 실행할 수 있습니다. ${access.reason}`;
}

export function IssueCandidatesPage({ api, access }: { api: AdminApi; access: SamsAccessState }) {
  const [status, setStatus] = useState<CandidateStatus | "all">("all");
  const [position, setPosition] = useState<StageView>("all");
  const [sourceEnvironment, setSourceEnvironment] = useState<CandidateSourceEnvironment | "all">("all");
  // Screen state only: a day's view is not something to bookmark or share.
  const [dateFilter, setDateFilter] = useState<DateFilter>({ preset: "all", from: "", to: "" });
  const [overview, setOverview] = useState<CandidateOverview | null>(null);
  const [items, setItems] = useState<Candidate[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [signals, setSignals] = useState<CandidateSignal[]>([]);
  const [signalCursor, setSignalCursor] = useState<string | null>(null);
  const [signalTotal, setSignalTotal] = useState<number | null>(null);
  const [signalCounts, setSignalCounts] = useState<SignalCounts>(null);
  const [signalError, setSignalError] = useState("");
  const [signalLoading, setSignalLoading] = useState(false);
  const [selected, setSelected] = useState<string | null>(candidateFromUrl);
  // [다시 시도] results by signal (environment + message id: the list keeps one row per origin),
  // kept across reloads: a fixed row may leave the list (VERIFY-55, VERIFY-57).
  const [retryNotes, setRetryNotes] = useState<Record<string, { environment: string; reason: string | null; line: RetryLine }>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detection, setDetection] = useState<{ items: JsonObject[]; failures: JsonObject[] } | null>(null);
  const [detecting, setDetecting] = useState(false);
  const [showDetection, setShowDetection] = useState(false);
  const detectionOff = detectionBlocked(access);

  // "전체" sends no filter: BE takes dev or prod only, and both is its default.
  const sourceEnv = sourceEnvironment === "all" ? undefined : sourceEnvironment;
  const { date_from, date_to } = dateRange(dateFilter);
  const showFeedback = position === "feedback";
  const showWaiting = position === "waiting";
  const showCandidates = position !== "signals" && !showFeedback;
  // Dropped signals sit under every stage, below the candidates; a status filter is about candidates only.
  const showSignals = position === "signals" || (position === "all" && status === "all");
  // The 1차 신호 card lists every signal with its result; under the candidates only the dropped ones.
  const signalOutcome = position === "signals" ? "all" : "not_promoted";
  const feedback = useFeedbackOnly(api, sourceEnv, showFeedback);

  const loadSignals = useCallback(
    async (next?: string | null) => {
      setSignalError("");
      setSignalLoading(true);
      try {
        const result = await api.listCandidateSignals({
          source_environment: sourceEnv, date_from, date_to, outcome: signalOutcome, cursor: next,
        });
        setSignals((current) => (next ? [...current, ...result.items] : result.items));
        setSignalCursor(result.next_cursor);
        setSignalTotal(result.total);
        return result.total;
      } catch (err) {
        setSignals([]);
        setSignalCursor(null);
        setSignalTotal(null);
        if (signalsUnavailable(err)) setSignalCounts("unavailable");
        else setSignalError(pipelineError(err));
        throw err;
      } finally {
        setSignalLoading(false);
      }
    },
    [api, sourceEnv, date_from, date_to, signalOutcome],
  );

  // The 1차 신호 card counts every signal and the dropped ones; the list shown supplies its own total.
  const loadSignalCounts = useCallback(async () => {
    const range = { source_environment: sourceEnv, date_from, date_to };
    const count = (outcome: "all" | "not_promoted") =>
      api.listCandidateSignals({ ...range, outcome, limit: 1 }).then((result) => result.total);
    try {
      const listed = showSignals ? loadSignals() : null;
      const [all, dropped] = await Promise.all([
        listed && signalOutcome === "all" ? listed : count("all"),
        listed && signalOutcome === "not_promoted" ? listed : count("not_promoted"),
      ]);
      setSignalCounts({ total: all, dropped });
    } catch (err) {
      setSignalCounts(signalsUnavailable(err) ? "unavailable" : null);
    }
  }, [api, sourceEnv, date_from, date_to, showSignals, signalOutcome, loadSignals]);

  const load = useCallback(
    async (next?: string | null) => {
      setLoading(true);
      setError("");
      try {
        if (!next) {
          // Counts are orientation, not the list: a failure leaves the cards blank.
          void api.getCandidateOverview({ source_environment: sourceEnv })
            .then(setOverview, () => setOverview(null));
          void loadSignalCounts();
          void feedback.load();
        }
        if (!showCandidates) {
          setItems([]);
          setCursor(null);
          return;
        }
        const result = await api.listCandidates({
          source_environment: sourceEnv,
          date_from,
          date_to,
          // A promoted candidate is the TC card; its status is implied there. TC 대기 is every pending one, in any stage.
          status: showWaiting ? "pending" : status === "all" || position === "tc" ? undefined : status,
          cursor: next,
          position: position === "all" || showWaiting ? undefined : position,
        });
        setItems((current) =>
          next ? [...current, ...result.items] : result.items,
        );
        setCursor(result.next_cursor);
      } catch (err) {
        setError(pipelineError(err));
      } finally {
        setLoading(false);
      }
    },
    [api, status, position, sourceEnv, date_from, date_to, showCandidates, showWaiting, loadSignalCounts, feedback.load],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const feed = mergeFeed(items, showSignals ? signals : [], { allSignals: position === "signals" });
  const signalsLabel = position === "signals" ? "1차 신호" : "TC 안 된 신호";

  return (
    <div className="grid gap-4">
      <CandidateStageOverview
        overview={overview}
        signals={signalCounts}
        feedback={feedbackOnlyCount(feedback.state)}
        feedbackOmitted={feedback.omitted}
        value={position}
        onChange={(next) => {
          setPosition(next);
          setSelected(null);
        }}
      />
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-sm whitespace-nowrap">
          처리 상태
          <select
            className={inputClass}
            value={position === "tc" ? "promoted" : showWaiting ? "pending" : status}
            disabled={position === "tc" || position === "signals" || showWaiting || showFeedback}
            onChange={(e) => {
              setStatus(e.target.value as CandidateStatus | "all");
              setSelected(null);
            }}
          >
            <option value="all">전체</option>
            {Object.entries(STATUSES).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm whitespace-nowrap">
          원본 환경
          <select
            className={inputClass}
            value={sourceEnvironment}
            onChange={(e) => {
              setSourceEnvironment(e.target.value as CandidateSourceEnvironment | "all");
              setSelected(null);
            }}
          >
            <option value="all">전체</option>
            <option value="dev">dev</option>
            <option value="prod">prod</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm whitespace-nowrap">
          일자 (KST)
          <select
            className={inputClass}
            value={dateFilter.preset}
            onChange={(e) => {
              setDateFilter((current) => ({ ...current, preset: e.target.value as DatePreset }));
              setSelected(null);
            }}
          >
            {Object.entries(DATE_PRESETS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {dateFilter.preset === "custom" && (
          <>
            <input
              type="date"
              aria-label="시작일"
              className={inputClass}
              value={dateFilter.from}
              onChange={(e) => setDateFilter((current) => ({ ...current, from: e.target.value }))}
            />
            <span className="text-sm text-muted-foreground">~</span>
            <input
              type="date"
              aria-label="종료일"
              className={inputClass}
              value={dateFilter.to}
              onChange={(e) => setDateFilter((current) => ({ ...current, to: e.target.value }))}
            />
          </>
        )}
        <Button
          variant="outline"
          disabled={loading}
          onClick={() => void load()}
        >
          새로고침
        </Button>
        <Button
          variant="outline"
          disabled={detectionOff !== null}
          aria-expanded={showDetection && detectionOff === null}
          onClick={() => setShowDetection((value) => !value)}
        >
          {showDetection && detectionOff === null ? "신호 탐지 닫기" : "신호 탐지 실행"}
        </Button>
        <Button
          variant="outline"
          disabled={detecting}
          aria-expanded={detection !== null}
          onClick={() => {
            if (detection !== null) {
              setDetection(null);
              return;
            }
            setDetecting(true);
            setError("");
            void api
              .listDetectionBatches()
              .then(setDetection)
              .catch((err) => setError(pipelineError(err)))
              .finally(() => setDetecting(false));
          }}
        >
          {detection !== null ? "실행 이력 닫기" : "탐지 실행 이력"}
        </Button>
      </div>
      {detectionOff && <p className="text-xs text-muted-foreground">{detectionOff}</p>}
      {showDetection && access.state === "admin" && <DetectionControls api={api} prodAccess={access.me.prod_access === true} />}
      {error && <Banner tone="error">{error}</Banner>}
      {dateFilter.preset !== "all" && (
        <p className="text-xs text-muted-foreground">
          일자 필터는 목록과 1차 신호 카드에 적용됩니다. 확정 TC 수는 전체 기간 기준이고, 피드백만은 일자와 무관한 현재 기록입니다.
        </p>
      )}
      {detection !== null && <DetectionHistory data={detection} api={api} onOpenCandidate={setSelected} />}
      <div
        className={`grid items-start gap-4 ${selected ? "xl:grid-cols-[minmax(280px,1fr)_minmax(440px,1.5fr)]" : ""}`}
      >
        <div className="grid gap-2">
          {Object.entries(retryNotes)
            .filter(([key]) => !feed.some((entry) => entry.kind === "signal" && signalKey(entry.item) === key))
            .map(([key, note]) => (
              <RetryNote key={`retry-${key}`} line={note.line}
                label={`다시 시도한 신호 (${note.environment} · ${note.reason ?? key})`} />
            ))}
          {feed.map((entry) => {
            if (entry.kind === "signal") {
              return <DroppedSignalCard key={entry.key} api={api} signal={entry.item} onOpenCandidate={setSelected}
                onPromoted={(candidateId) => { setSelected(candidateId); void load(); }}
                retryNote={retryNotes[signalKey(entry.item)]?.line}
                onRetried={(line) => {
                  const signal = entry.item;
                  setRetryNotes((notes) => ({ ...notes,
                    [signalKey(signal)]: { environment: signal.source_environment, reason: signal.reason, line } }));
                  void load();
                }} />;
            }
            const item = entry.item;
            const where = positionOf(item);
            const summary = item.reproduction_summary;
            const verdict = where === "second" ? verdictLabel(item) : null;
            return (
              <div
                key={item.candidate_id}
                className={`overflow-hidden rounded-xl border ${selected === item.candidate_id ? "border-primary bg-primary/5" : "border-border bg-card"}`}
              >
                <button
                  type="button"
                  onClick={() => setSelected(item.candidate_id)}
                  className="grid w-full gap-2 p-4 text-left"
                >
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge size="compact" variant={where === "tc" ? "accent" : "default"}>
                      {badgeLabel(item, where)}
                    </Badge>
                    <Badge size="compact">{sourceLabel(item)}</Badge>
                    <Badge size="compact">{STATUSES[item.status]}</Badge>
                    {verdict && <Badge size="compact">{verdict}</Badge>}
                    {summary && (
                      <Badge size="compact">
                        이전 방식 재현 {summary.reproduced}/{summary.trials}
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {item.problem_type} · {item.source_environment}
                    </span>
                  </div>
                  <p className="line-clamp-3 text-sm whitespace-pre-wrap">{item.description}</p>
                  <span className="text-xs text-muted-foreground">
                    {new Date(item.updated_at).toLocaleString()} · 근거{" "}
                    {item.evidence.length}개
                  </span>
                  {item.missing_information.length > 0 && (
                    <span className="text-xs text-warning-foreground">
                      보완: {item.missing_information.join(" · ")}
                    </span>
                  )}
                </button>
                {waitsForTc(item) && (
                  <div className="flex flex-wrap items-center gap-2 border-t border-border px-4 py-2 text-xs">
                    <span className="text-muted-foreground">자동 TC 결과와 실패 이유는 후보를 열면 보입니다.</span>
                    <Button size="sm" variant="outline" className="ml-auto" onClick={() => setSelected(item.candidate_id)}>
                      수동 TC 승격
                    </Button>
                  </div>
                )}
                {item.test_case_id && (
                  <div className="border-t border-border px-4 py-2 text-xs">
                    <a
                      className="text-link hover:underline"
                      href={`/test-cases/all?tc=${encodeURIComponent(item.test_case_id)}`}
                    >
                      {item.status === "linked" ? "연결된 TC" : "승격된 TC"} 열기 →
                    </a>
                  </div>
                )}
              </div>
            );
          })}
          {(loading || (showSignals && signalLoading)) && (
            <p role="status" className="text-sm">
              불러오는 중…
            </p>
          )}
          {showFeedback && <FeedbackOnlyPanel state={feedback.state} omitted={feedback.omitted} onMore={(environment) => void feedback.more(environment)} />}
          {!showFeedback && !loading && !(showSignals && signalLoading) && feed.length === 0 && (
            <Card className="p-5 text-sm text-muted-foreground">
              {position === "signals" ? "이 조건의 1차 신호가 없습니다." : showWaiting ? "TC를 기다리는 후보가 없습니다." : "이 조건의 후보가 없습니다."}
            </Card>
          )}
          {cursor && showCandidates && (
            <Button
              variant="outline"
              disabled={loading}
              onClick={() => void load(cursor)}
            >
              후보 더 보기
            </Button>
          )}
          {showSignals && <SignalStatusBreakdown signals={signals} total={signalTotal} />}
          {showSignals && signalCounts === "unavailable" && (
            <Card className="p-4 text-sm text-muted-foreground">
              {signalsLabel} 목록은 서버에 신호 목록 API가 배포되면 이 목록에 함께 보입니다. 지금은
              「탐지 실행 이력」에서 실행별로 확인하고 수동 승격할 수 있습니다.
            </Card>
          )}
          {showSignals && signalError && <Banner tone="error">{signalsLabel}: {signalError}</Banner>}
          {showSignals && signalCursor && (
            <Button
              variant="outline"
              disabled={loading || signalLoading}
              onClick={() => void loadSignals(signalCursor).catch(() => undefined)}
            >
              {position === "signals" ? "신호 더 보기" : "TC 안 된 신호 더 보기"}
            </Button>
          )}
        </div>
        {selected && (
          <CandidateDetail
            key={selected}
            api={api}
            id={selected}
            listStage={items.find((row) => row.candidate_id === selected)?.pipeline_stage}
            onClose={() => setSelected(null)}
            onReviewed={() => void load()}
            onSplit={(candidate) => { setSelected(candidate.candidate_id); void load(); }}
          />
        )}
      </div>
    </div>
  );
}

export function CandidateDetail({
  api,
  id,
  listStage,
  onClose,
  onReviewed,
  onSplit,
}: {
  api: AdminApi;
  id: string;
  /** The stage the list showed, for older servers whose detail response does not carry it. */
  listStage?: "first" | "second";
  onClose: () => void;
  onReviewed: () => void;
  onSplit: (candidate: Candidate) => void;
}) {
  const [item, setItem] = useState<Candidate | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [promotionReason, setPromotionReason] = useState("");
  const [promotionCriteria, setPromotionCriteria] = useState("{}");
  const [retryResult, setRetryResult] = useState<RetryLine | null>(null);
  useEffect(() => setRetryResult(null), [id]);
  useEffect(() => {
    let cancelled = false;
    void api
      .getCandidate(id)
      .then(({ item: next }) => {
        if (cancelled) return;
        setItem(next);
        setPromotionCriteria(JSON.stringify(next.criteria_draft || {}, null, 2));
      })
      .catch((err) => {
        if (!cancelled) setError(pipelineError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [api, id]);

  const result = item?.analysis?.result as JsonObject | undefined;
  // The detail response says the stage, so a link opened straight away is judged the same as from the list.
  const stage = item?.pipeline_stage ?? listStage;
  const where = item ? positionOf({ ...item, pipeline_stage: stage }) : null;
  const verdict = item && where === "second" ? verdictLabel(item) : null;
  const failures = item ? candidateFailures(item) : [];
  const reviewReason = item?.review_reason?.trim() || null;
  // An automatic TC (1.1차 pass or 👎) that did not happen: an error row, retried at once.
  const retryable = Boolean(item && !item.test_case_id && (item.status === "pending" || item.status === "needs_completion")
    && (item.issue_key === "negative_feedback" || (item.sources ?? []).some((source) => source.source_kind === "ai_observation")));
  const promote = async () => {
    if (!item || busy || !promotionReason.trim()) return;
    setError("");
    setBusy(true);
    try {
      const result = await api.reviewCandidate(id, {
        action: "promote", reason: promotionReason.trim(),
        criteria: parseObject(promotionCriteria, "채점 기준"),
      });
      setItem((current) => ({ ...current!, ...result.item }));
      setPromotionReason("");
      onReviewed();
    } catch (err) {
      setError(pipelineError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="grid gap-5 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="grid gap-1.5">
          <h2 className="font-semibold">후보 기록</h2>
          {item && (
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge size="compact" variant={where === "tc" ? "accent" : "default"}>
                {badgeLabel(item, where!)}
              </Badge>
              <Badge size="compact">{sourceLabel(item)}</Badge>
              <Badge size="compact">{STATUSES[item.status]}</Badge>
              {verdict && <Badge size="compact">{verdict}</Badge>}
              <span className="break-all text-[11px] text-muted-foreground">{item.candidate_id}</span>
            </div>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          닫기
        </Button>
      </div>
      {error && <Banner tone="error">{error}</Banner>}
      {!item ? (
        <p className="text-sm">불러오는 중…</p>
      ) : (
        <>
          <DetailSection step="1" title="문제">
            <p className="whitespace-pre-wrap text-sm">{item.description}</p>
            {item.expected_behavior && (
              <p className="text-sm text-muted-foreground">기대 동작 · {item.expected_behavior}</p>
            )}
            {item.original_outcome && (
              <p className="text-sm text-muted-foreground" aria-label="원래 응답 상태">
                원래 응답 상태: {{
                  ai_not_invoked: 'AI 미호출 · 대상 제외',
                  answer_present: '답변 있음',
                  intentional_silence: '의도적 침묵',
                  execution_failed: 'AI 호출 후 답변 없음 · 실행 실패',
                  invocation_unknown: '호출 기록 부족 · 판정 미확인',
                }[item.original_outcome.classification]}
              </p>
            )}
            {item.missing_information.length > 0 && (
              <p className="rounded border border-warning p-3 text-sm">
                누락 자료: {item.missing_information.join(" · ")}
              </p>
            )}
            <FeedbackSummary feedback={result?.feedback} legacy={(result?.issues as JsonObject[] | undefined)?.find((issue) => issue.issue_key === item.issue_key)?.judgment || result?.judgment} />
          </DetailSection>

          <DetailSection step="2" title="근거 대화">
            {item.source_load_error && (
              <Banner tone="error">
                원본 기록 조회 실패: {item.source_load_error}
              </Banner>
            )}
            <EvidenceConversation item={item} />
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
              {item.session_id && (
                <a
                  className="text-link hover:underline"
                  href={conversationHref(
                    item.source_environment,
                    item.session_id,
                    item.source_message_id || item.assistant_message_id || item.trigger_message_id,
                  )}
                >
                  원본 conversation · Trace / raw 보기 ↗
                </a>
              )}
              {item.source_test_case_id && (
                <a className="text-link hover:underline" href={`/test-cases/all?tc=${encodeURIComponent(item.source_test_case_id)}`}>
                  출처 TC (중복 검토) ↗
                </a>
              )}
              {item.test_case_id && (
                <a className="text-link hover:underline" href={`/test-cases/all?tc=${encodeURIComponent(item.test_case_id)}`}>
                  확정 TC ↗
                </a>
              )}
            </div>
          </DetailSection>

          <DetailSection step="3" title="TC 생성 상태">
            <p className="text-sm">1차 → 1.1차 통과 → 자동 TC · 👎 → 바로 TC</p>
            {item.test_case_id ? <p className="text-sm">연결된 TC가 있습니다. 위 TC 링크에서 확인하세요.</p> : <>
              <p className="text-sm">
                {waitsForTc(item) && !failures.length ? "TC 대기 (자동 TC 시도 기록 없음)" : `연결된 TC 없음 · ${STATUSES[item.status]}`}
              </p>
              {failures.length ? failures.map((failure, index) => <Banner key={index} tone="error">
                {failure.sourceKind === "auto_tc_failure" ? "자동 TC 생성 실패" : "원본 캡처 실패"}: {[failure.code, failure.message].filter(Boolean).join(" · ")}
              </Banner>) : !reviewReason && (waitsForTc(item) ? <p className="text-xs text-muted-foreground">
                자동 TC를 시도했거나 실패한 기록이 없습니다.{(stage === "second" || item.generalization_job) && " 이전 방식(2차)을 기다리다 멈춘 후보입니다."} 아래에서 직접 TC로 승격할 수 있습니다.
              </p> : <p className="text-xs text-muted-foreground">후보별 자동 TC 실패 사유가 응답에 없습니다. 탐지 실행 이력과 원본 기록을 확인하세요.</p>)}
              {retryable && <RetryButton run={() => api.retryCandidate(id)} onSettled={async (line) => {
                setRetryResult(line);
                const refreshed = await api.getCandidate(id).catch(() => null);
                if (refreshed) setItem(refreshed.item);
                onReviewed();
              }} />}
            </>}
            {retryResult && <RetryNote line={retryResult} />}
            {reviewReason && <p className="text-sm">기록된 검토 사유: {reviewReason}</p>}
            <JsonDetails title="저장된 채점 기준" value={item.criteria_draft} />
            {(item.status === "pending" || item.status === "needs_completion") && !item.test_case_id && (
              <fieldset disabled={busy} className="grid gap-3 rounded-lg border border-border p-3">
                <legend className="px-1 text-sm font-medium">수동 TC 승격</legend>
                <p className="text-xs text-muted-foreground">자동으로 걸러졌거나 TC 생성에 실패한 신호도 원본 질문과 채점 기준을 확인한 뒤 직접 승격할 수 있습니다.</p>
                <Field label="승격 사유 (필수)" value={promotionReason} onChange={setPromotionReason} multiline />
                <Field label="채점 기준 (judge v2 JSON)" value={promotionCriteria} onChange={setPromotionCriteria} multiline rows={6} />
                <Button className="justify-self-start" disabled={busy || !promotionReason.trim()} onClick={() => void promote()}>TC 승격</Button>
              </fieldset>
            )}
          </DetailSection>

          <DetailSection title="후보 정리">
            <CandidateOrganization api={api} item={item} disabled={busy} onBusy={setBusy} onSplit={onSplit} onMerged={async () => {
              const refreshed = await api.getCandidate(id);
              setItem(refreshed.item); onReviewed();
            }} />
          </DetailSection>

          <DetailSection title="원본 기록">
            <JsonDetails title="탐지 분석 · 프롬프트 버전" value={item.analysis} />
            <JsonDetails title="근거 메시지 원문" value={item.cited_messages} />
            <JsonDetails title="원본 대화" value={item.conversation} />
            <JsonDetails title="실행 기록 · 도구 · 서브에이전트" value={item.execution} />
            <JsonDetails title="출처 연결" value={item.sources || []} />
            <JsonDetails title="연결된 원본 보고" value={item.organization_sources || []} />
            <JsonDetails title="검토 이력" value={item.reviews || []} />
          </DetailSection>
        </>
      )}
    </Card>
  );
}

function DetailSection({ step, title, children }: { step?: string; title: string; children: React.ReactNode }) {
  return (
    <section className="grid gap-2.5 border-t border-border pt-4 first:border-t-0 first:pt-0" aria-label={title}>
      <h3 className="flex items-center gap-2 text-sm font-semibold">
        {step && (
          <span className="grid size-5 place-items-center rounded-full border border-border text-[11px] font-normal text-muted-foreground">
            {step}
          </span>
        )}
        {title}
      </h3>
      {children}
    </section>
  );
}

/**
 * The cited messages as a short conversation, not as ids: who said what,
 * which one is the complaint, the question and the answer, and the AI's note.
 */
function EvidenceConversation({ item }: { item: Candidate }) {
  const messages = Array.isArray(item.cited_messages) ? item.cited_messages : [];
  const notes = new Map(item.evidence.filter((e) => e.kind === "message").map((e) => [e.id, e.note]));
  const roleOf = (id: string) =>
    id === item.source_message_id && id !== item.trigger_message_id ? "불만 신호"
      : id === item.trigger_message_id ? "원래 질문"
      : id === item.assistant_message_id ? "AI 답변" : null;
  if (!messages.length) {
    return item.evidence.length ? (
      <ul className="grid gap-1.5 text-xs">
        {item.evidence.map((e, i) => (
          <li key={`${e.kind}-${e.id}-${i}`} className="rounded border border-border p-2">
            <span className="break-all text-muted-foreground">{e.kind} · {e.id}</span>
            {e.note && <p className="mt-1 whitespace-pre-wrap">{e.note}</p>}
          </li>
        ))}
      </ul>
    ) : (
      <p className="text-xs text-muted-foreground">기록된 근거가 없습니다.</p>
    );
  }
  return (
    <ol className="grid gap-2">
      {messages.map((raw) => {
        const message = raw as JsonObject;
        const id = String(message.id);
        const isAi = message.role === "assistant";
        const tag = roleOf(id);
        const note = notes.get(id);
        return (
          <li key={id} className={`grid gap-1 rounded-lg border p-2.5 text-sm ${isAi ? "border-border bg-surface-subtle" : "border-border bg-card"} ${tag === "불만 신호" ? "border-accent-line" : ""}`}>
            <span className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="font-medium text-foreground">{isAi ? "AI" : "사용자"}</span>
              {tag && <Badge size="compact" variant={tag === "불만 신호" ? "accent" : "default"}>{tag}</Badge>}
              {typeof message.created_at === "string" && new Date(message.created_at).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" })}
              {item.session_id && (
                <a className="ml-auto text-link hover:underline" href={conversationHref(item.source_environment, item.session_id, id)}>
                  메시지 열기 ↗
                </a>
              )}
            </span>
            <p className="line-clamp-6 whitespace-pre-wrap">{String(message.content ?? "")}</p>
            {note && <p className="text-xs text-muted-foreground">근거 · {note}</p>}
          </li>
        );
      })}
    </ol>
  );
}

function CandidateOrganization({ api, item, disabled, onBusy, onSplit, onMerged }: {
  api: AdminApi; item: Candidate; disabled: boolean;
  onBusy: (busy: boolean) => void;
  onSplit: (candidate: Candidate) => void; onMerged: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [expected, setExpected] = useState("");
  const [problemType, setProblemType] = useState(item.problem_type);
  const [splitReason, setSplitReason] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [mergeReason, setMergeReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const splitRequest = useRef<{ signature: string; key: string } | null>(null);
  const mergeRequest = useRef<{ signature: string; key: string } | null>(null);
  const editable = ["pending", "needs_completion"].includes(item.status);
  const unavailable = disabled || busy || !editable;
  const change = (action: "split" | "merge", setter: (value: string) => void) => (value: string) => {
    (action === "split" ? splitRequest : mergeRequest).current = null;
    setError(""); setSaved(""); setter(value);
  };
  const submit = async (action: "split" | "merge") => {
    if (unavailable) return;
    setError(""); setSaved("");
    const reason = (action === "split" ? splitReason : mergeReason).trim();
    if (!reason || reason.length > 8000) { setError("작업 사유를 1~8,000자로 입력해 주세요."); return; }
    if (action === "split" && (!description.trim() || description.length > 12000 || expected.length > 12000 || !problemType.trim())) {
      setError("분리할 문제 설명과 유형을 입력하세요. 설명·기대 동작은 각각 12,000자까지입니다."); return;
    }
    if (action === "merge" && (!sourceId.trim() || sourceId.trim() === item.candidate_id)) {
      setError("출처를 가져올 다른 후보 ID를 입력해 주세요."); return;
    }
    const payload = action === "split" ? { description: description.trim(), expected_behavior: expected, problem_type: problemType.trim(), reason }
      : { source_candidate_id: sourceId.trim(), reason };
    const ref = action === "split" ? splitRequest : mergeRequest;
    const signature = JSON.stringify(payload);
    if (ref.current?.signature !== signature) ref.current = { signature, key: crypto.randomUUID() };
    const operation_key = ref.current!.key;
    setBusy(true); onBusy(true);
    try {
      if (action === "split") {
        const result = await api.splitCandidate(item.candidate_id, { operation_key, description: description.trim(), expected_behavior: expected, problem_type: problemType.trim(), reason });
        onSplit(result.item);
      } else {
        await api.mergeCandidateSources(item.candidate_id, { operation_key, source_candidate_id: sourceId.trim(), reason });
        await onMerged();
        setSaved("다른 후보의 출처를 연결했습니다. 두 후보는 보존됩니다.");
      }
      ref.current = null;
    } catch (err) { setError(pipelineError(err)); }
    finally { setBusy(false); onBusy(false); }
  };
  return <section aria-label="후보 분할 및 출처 묶음" className="grid gap-3 border-t pt-3 text-sm">
    <Button variant="outline" disabled={disabled || busy} onClick={() => setOpen((value) => !value)}>후보 분할 · 출처 묶음</Button>
    {open && <>
      <p>별개의 문제는 새 검토 후보로 분리하고, 같은 문제의 다른 후보는 출처를 묶습니다. 원본은 보존합니다. 실행 입력과 일반화 검증은 자동으로 합치거나 재사용하지 않습니다.</p>
      {!editable && <p>처리 대기·보완 필요 후보에서만 분할하거나 출처를 묶을 수 있습니다.</p>}
      {error && <Banner tone="error">{error}</Banner>}
      {saved && <p role="status">{saved}</p>}
      <fieldset disabled={unavailable} className="grid gap-2 rounded border p-3">
        <legend>별개의 문제 분리</legend>
        <Field label="분리할 문제 설명" value={description} onChange={change("split", setDescription)} multiline />
        <Field label="분리할 기대 동작" value={expected} onChange={change("split", setExpected)} multiline />
        <Field label="분리할 문제 유형" value={problemType} onChange={change("split", setProblemType)} />
        <Field label="분할 사유 (필수)" value={splitReason} onChange={change("split", setSplitReason)} multiline />
        <Button variant="outline" onClick={() => void submit("split")}>새 후보로 분리</Button>
      </fieldset>
      <fieldset disabled={unavailable} className="grid gap-2 rounded border p-3">
        <legend>같은 문제의 출처 묶음</legend>
        <Field label="출처를 가져올 후보 ID" value={sourceId} onChange={change("merge", setSourceId)} />
        <Field label="출처 묶음 사유 (필수)" value={mergeReason} onChange={change("merge", setMergeReason)} multiline />
        <Button variant="outline" onClick={() => void submit("merge")}>현재 후보에 출처 연결</Button>
      </fieldset>
    </>}
  </section>;
}
